import { DynamicEmbeddedWidget } from '@dynamic-labs/sdk-react-core';
import Link from 'next/link';
import { NextPageWithLayout } from 'pages/_app';
import { ReactElement, useEffect } from 'react';
import { useConnectors } from 'src/common/context/ConnectorsContext';
import { AuthLayout } from 'src/layouts/auth/auth-layout';
import { InitAuthLayout } from 'src/layouts/auth/init-auth-layout';
import DynamicEmbeddedWidgetSkeleton from 'src/modules/auth/components/DynamicEmbeddedWidgetSkeleton';
import { useAuth } from 'src/modules/auth/context/AuthContext';

import { inter } from '@/context/theme/create-typography';
import { Card, Spinner, Text } from '@/design-system/components/ui';

const LoginPage: NextPageWithLayout = (): ReactElement => {
  const { signIn, isAuthenticated, isAuthenticating, authError } = useAuth();
  const { areConnectorsLoading } = useConnectors();

  useEffect(() => {
    // Call signIn when authenticated to trigger redirect logic
    // This handles both first login and post-refresh redirects
    if (isAuthenticated) {
      signIn();
    }
  }, [signIn, isAuthenticated]);

  // Show simple loading if user is authenticating
  if (isAuthenticated || isAuthenticating) {
    return (
      <InitAuthLayout>
        <div className="tw:flex tw:items-center tw:my-4">
          <Spinner />
        </div>
      </InitAuthLayout>
    );
  }

  // Show login form with skeleton while connectors are loading
  return (
    <InitAuthLayout>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:w-full tw:max-w-[430px]">
        <Card className="tw:w-full tw:flex tw:justify-center">
          {areConnectorsLoading ? (
            <DynamicEmbeddedWidgetSkeleton />
          ) : (
            <DynamicEmbeddedWidget style={{ fontFamily: inter.style.fontFamily, width: '100%' }} />
          )}
        </Card>
        <div className="tw:w-full">
          <Text variant="caption" color="muted">
            I acknowledge that I have read and agree to the applicable{' '}
            <Link href={'https://www.fuul.xyz/terms'} target="_blank" className="tw:text-fuul-blue tw:underline">
              Terms of Use
            </Link>
            .
          </Text>
        </div>
        {authError && (
          <Text variant="body-md" color="error" className="tw:text-center">
            Error: {authError}
          </Text>
        )}
      </div>
    </InitAuthLayout>
  );
};

export default LoginPage;

LoginPage.getLayout = function getLayout(page: ReactElement) {
  return <AuthLayout>{page}</AuthLayout>;
};
